import type { EntityType, NoteEntityCandidate } from '../../types';

/**
 * Internal identifiers that must never surface in the UI (OMNI-634).
 *
 * The note-draft action's `candidates[].label` is server-built and falls back
 * to the raw entity id when it can't resolve a friendly number, producing
 * "Quote — 4099f1ed-da36-47bf-a095-b23235084129" in the "Save to" selector.
 * We treat any label carrying an id-shaped token as unusable and rebuild it
 * client-side instead of rendering it.
 *
 * Covers dashed UUIDs, dash-stripped 32-char UUIDs and 24-char ObjectIds.
 */
const INTERNAL_ID_RE =
  /\b(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32}|[0-9a-f]{24})\b/i;

export function containsInternalId(value: string | undefined): boolean {
  return !!value && INTERNAL_ID_RE.test(value);
}

/** A server label is only safe to render when it exists and leaks no id. */
export function isSafeCandidateLabel(label: string | undefined): boolean {
  return !!label?.trim() && !containsInternalId(label);
}

const TYPE_LABELS: Record<NoteEntityCandidate['entityType'], string> = {
  ACCOUNT: 'Account',
  QUOTE: 'Quote',
  SUBMISSION: 'Submission',
  CLAIM: 'Claim',
};

const TYPE_TO_ENTITY_TYPE: Record<
  NoteEntityCandidate['entityType'],
  EntityType
> = {
  ACCOUNT: 'account',
  QUOTE: 'quote',
  SUBMISSION: 'submission',
  CLAIM: 'claim',
};

export function toEntityType(
  entityType: NoteEntityCandidate['entityType']
): EntityType | undefined {
  return TYPE_TO_ENTITY_TYPE[entityType];
}

/**
 * The label shown in the "Save to" selector.
 *
 * Prefers the server label, but when that would leak an internal id it is
 * rebuilt from the entity's friendly number/name (resolved via
 * `useEntityLabel`). While that lookup is in flight — or if it fails — we fall
 * back to the bare entity type, which is never an id.
 */
export function buildSafeCandidateLabel(
  candidate: NoteEntityCandidate,
  resolved?: { number?: string; name?: string }
): string {
  if (isSafeCandidateLabel(candidate.label)) return candidate.label;

  const typeLabel = TYPE_LABELS[candidate.entityType] ?? 'Record';
  const identifier = resolved?.number || resolved?.name;
  return identifier ? `${typeLabel} — ${identifier}` : typeLabel;
}
